import type { Language } from '@src/types.ts/language';

// BCP-47 code for the "regular English" every workspace offers by default.
const EN_US = 'en-US';

// Fallback en-US record, used only when a workspace's config omits English (US)
// yet an English-curated voice still needs its by-default English row. A real
// workspace record is preferred whenever one is present.
const DEFAULT_EN_US: Language = {
  id: EN_US,
  caption: 'English (US)',
  value: EN_US,
  baseLanguage: 'en',
  region: 'US',
  script: null,
  rtl: false,
  handlerHint: null,
  defaultModel: null,
  isActive: true,
};

// Any English variant (`en`, `en-US`, `en-GB`, …). Used to detect an
// English-curated voice and to strip English out of the base groups before the
// workspace's English variants are re-added explicitly.
export function isEnglish(l: Language): boolean {
  const v = l.value?.toLowerCase() ?? '';
  return (
    l.baseLanguage?.toLowerCase() === 'en' || v === 'en' || v.startsWith('en-')
  );
}

export interface LanguageGroups {
  /** Voice languages that intersect the workspace's languages — shown first. */
  workspaceGroup: Language[];
  /** The remaining voice languages. */
  otherGroup: Language[];
}

/**
 * The voice's supported languages, split into workspace-active (highlighted,
 * first) vs the rest (DI19 advisory — never a hard gate). Falls back to the
 * workspace's languages when the voice lists none.
 *
 * English handling (PLAT-10576): English is a **workspace default** (every
 * workspace offers English (US) / `en-US`, and may opt in to English (UK) /
 * `en-GB`), surfaced **only for voices curated for English** (any `en-*` in
 * their supported languages). For such a voice we show the **workspace's**
 * English variants as distinct rows — because AI Voices are multilingual
 * (DI19), an English-curated voice can speak whichever English the workspace
 * uses. A voice not curated for English gets no English row. Shared by
 * `VoiceAuditionPanel` and `VoiceInfoCard`.
 */
export function languageGroups(
  voiceLangs: Language[],
  workspaceLanguages: Language[],
): LanguageGroups {
  const workspaceValues = new Set(workspaceLanguages.map(l => l.value));
  let ws: Language[];
  let other: Language[];
  if (voiceLangs.length === 0) {
    ws = [...workspaceLanguages];
    other = [];
  } else {
    ws = voiceLangs.filter(l => workspaceValues.has(l.value));
    other = voiceLangs.filter(l => !workspaceValues.has(l.value));
  }

  // English rows are added explicitly below (the workspace's variants), so
  // strip any English out of the base groups first — this keeps us in control
  // of exactly which English variants appear and avoids listing the voice's own
  // English record separately from the workspace's.
  ws = ws.filter(l => !isEnglish(l));
  other = other.filter(l => !isEnglish(l));

  // Only an English-curated voice gets English (a voice with no listed
  // languages falls back to the workspace and is treated as eligible). "If a
  // voice doesn't support it, there's no need to create an English version."
  const voiceSupportsEnglish =
    voiceLangs.length === 0 || voiceLangs.some(isEnglish);
  if (voiceSupportsEnglish) {
    // The workspace's English variants: en-US by default, en-GB (and any other)
    // where the workspace has opted in. Guarantee en-US even if the workspace
    // config omitted it.
    const englishRows = workspaceLanguages.filter(isEnglish);
    if (!englishRows.some(l => l.value === EN_US)) {
      englishRows.unshift(DEFAULT_EN_US);
    }
    ws = [...ws, ...englishRows];
  }

  return { workspaceGroup: ws, otherGroup: other };
}
